import type { ApiPaginatedResponse, ApiSuccessResponse } from '../types/api'

export type PaginationMeta = ApiPaginatedResponse<unknown>['meta']

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 150] as const

export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number]

export const DEFAULT_PAGE_SIZE: PageSizeOption = 10

export function extractList<T>(
  response: ApiPaginatedResponse<T> | ApiSuccessResponse<T[]>,
): { items: T[]; meta: PaginationMeta | null } {
  const items = Array.isArray(response.data) ? response.data : []
  const meta =
    'meta' in response && response.meta && typeof response.meta === 'object'
      ? response.meta
      : null

  return { items, meta }
}

/** When the API returns a plain array (no meta), paginate locally. */
export function paginateLocally<T>(
  items: T[],
  page: number,
  perPage: number,
): { items: T[]; meta: PaginationMeta } {
  const total = items.length
  const lastPage = Math.max(1, Math.ceil(total / perPage) || 1)
  const currentPage = Math.min(Math.max(1, page), lastPage)
  const start = (currentPage - 1) * perPage

  return {
    items: items.slice(start, start + perPage),
    meta: {
      current_page: currentPage,
      per_page: perPage,
      total,
      last_page: lastPage,
    },
  }
}

export function resolveListPage<T>(
  response: ApiPaginatedResponse<T> | ApiSuccessResponse<T[]>,
  page: number,
  perPage: number,
): { items: T[]; meta: PaginationMeta } {
  const { items, meta } = extractList(response)

  if (meta) {
    return { items, meta }
  }

  return paginateLocally(items, page, perPage)
}
